<template>
	<Teleport to="body">
		<div class="login-wrapper" @click.self="emit('dismiss')">
			<div class="form">
				<div class="photo-header">
					<img :src="landerzMarkUri" class="brand-mark" alt="Landerz" />
					<h1>{{ t("loginPage.title") }}</h1>
				</div>

				<div class="login-content">
					<p class="message">{{ showFeatures ? t("loginPage.message") : t("loginPage.subtitle") }}</p>

					<ul v-if="showFeatures" class="features">
						<li v-for="feature in features" :key="feature.key">
							<span class="icon"><AppIcon :name="feature.icon" :size="14" /></span>
							{{ t(`loginPage.features.${feature.key}`) }}
						</li>
					</ul>

					<div v-if="infoMessage" class="info-message">
						{{ infoMessage }}
					</div>

					<!-- Toggle between Login and Signup -->
					<!-- <div class="auth-toggle">
						<button :class="{ active: !isSignup }" @click="isSignup = false" type="button">Login</button>
						<button :class="{ active: isSignup }" @click="isSignup = true" type="button">Sign Up</button>
					</div> -->

					<!-- Login Form -->
					<!-- <form class="auth-form" @submit.prevent="logIn" v-if="!isSignup" autocomplete="on">
						<fieldset>
							<label for="login-email">Email</label>
							<input
								id="login-email"
								type="email"
								v-model="loginDetails.email"
								aria-label="Email"
								autocomplete="email"
								name="email"
								required
							/>
						</fieldset>

						<fieldset>
							<label for="login-password">Password</label>
							<input
								type="password"
								id="login-password"
								v-model="loginDetails.password"
								aria-label="Password"
								autocomplete="current-password"
								name="password"
								required
							/>
						</fieldset>

						<button type="submit" :disabled="isSubmitting">Log In</button>
					</form> -->

					<!-- Signup Form -->
					<!-- <form class="auth-form" @submit.prevent="signUp" v-if="isSignup" autocomplete="on">
						<fieldset>
							<label for="signup-email">Email</label>
							<input
								id="signup-email"
								type="email"
								v-model="signupDetails.email"
								aria-label="Email"
								autocomplete="email"
								name="email"
								required
							/>
						</fieldset>

						<fieldset>
							<label for="signup-password">Password</label>
							<input
								type="password"
								id="signup-password"
								v-model="signupDetails.password"
								aria-label="Password"
								autocomplete="new-password"
								name="new-password"
								required
							/>
						</fieldset>

						<fieldset>
							<label for="signup-confirm-password">Confirm Password</label>
							<input
								type="password"
								id="signup-confirm-password"
								v-model="signupDetails.confirmPassword"
								aria-label="Confirm Password"
								autocomplete="new-password"
								name="confirm-password"
								required
							/>
						</fieldset>

						<button type="submit" :disabled="isSubmitting">Sign Up</button>
					</form> -->

					<!-- Social Login -->
					<!-- <div class="divider">
						<span>or</span>
					</div> -->

					<div class="providers">
						<button type="button" @click="emit('google')" :disabled="isSubmitting" class="provider">
							<AppIcon :name="IconName.Google" :size="18" />
							{{ t("loginPage.google") }}
						</button>

						<!-- LinkedIn (feature flag) -->
						<!-- <button
						type="button"
						@click="signInWithLinkedIn"
						:disabled="isSubmitting"
						class="social-login-btn linkedin-btn"
					>
						<AppIcon :name="IconName.LinkedIn" :size="20" />
						Sign in with LinkedIn
					</button> -->

						<!-- Outlook/Microsoft (feature flag) -->
						<button type="button" @click="emit('microsoft')" :disabled="isSubmitting" class="provider">
							<AppIcon :name="IconName.Microsoft" :size="18" />
							{{ t("loginPage.microsoft") }}
						</button>
					</div>

					<div v-if="authError" class="error-message">
						{{ authError }}
					</div>

					<Button variant="ghost" size="m" class="guest" @click="emit('dismiss')">
						{{ t("loginPage.continueAsGuest") }}
					</Button>

					<Button variant="ghost" size="xs" class="privacy" @click="privacy.openSettings()">
						{{ t("privacy.settings") }}
					</Button>
				</div>
			</div>
		</div>
	</Teleport>
</template>

<script setup lang="ts">
import AppIcon from "@/components/AppIcon.vue"
import Button from "@/components/ui/Button.vue"
import { IconName } from "@/components/icons/registry"
import { useI18n } from "vue-i18n"
import { buildLanderzMarkDataUri } from "@/utils/opportunityIcons"
import { usePrivacyStore } from "@/stores/privacyStore"

defineProps<{
	isSubmitting: boolean
	authError?: string
	infoMessage?: string
	showFeatures?: boolean
}>()

const emit = defineEmits<{ google: []; microsoft: []; dismiss: [] }>()

const features = [
	{ key: "realEstateData", icon: IconName.MapPin },
	{ key: "exclusiveOpportunities", icon: IconName.Target },
	{ key: "savedSearches", icon: IconName.Bookmark },
]

const { t } = useI18n()
const privacy = usePrivacyStore()
const landerzMarkUri = buildLanderzMarkDataUri()
</script>

<style scoped>
.login-wrapper {
	position: fixed;
	inset: 0;
	z-index: 1000;
	overflow-y: auto;
	background-color: var(--overlay-backdrop);
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	padding: var(--spacing-xl) var(--spacing-md);

	.form {
		width: 100%;
		max-width: 400px;
		overflow: hidden;
		border-radius: var(--border-radius-lg);
		background: var(--neutral-0);
		box-shadow: var(--shadow-xl);
	}

	.photo-header {
		position: relative;
		height: var(--space-100);
		background-image: linear-gradient(180deg, rgb(6 32 22 / 15%) 0%, rgb(6 32 22 / 75%) 100%),
			url("@/assets/images/terrain-stock.jpg");
		background-size: cover;
		background-position: center 65%;

		h1 {
			position: absolute;
			left: calc(var(--spacing-lg) + var(--space-10));
			right: var(--spacing-lg);
			bottom: calc(var(--space-24) + var(--spacing-md));
			margin: 0;
			font-size: var(--font-size-large);
			font-weight: var(--font-weight-bold);
			color: var(--neutral-0);
			text-shadow: 0 1px 4px rgb(0 0 0 / 40%);
		}
	}

	.brand-mark {
		position: absolute;
		left: var(--spacing-lg);
		bottom: calc(var(--space-24) * -1);
		width: var(--space-48);
		height: var(--space-48);
		object-fit: contain;
		filter: drop-shadow(0 4px 10px rgb(0 0 0 / 28%));
	}

	.login-content {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-md);
		padding: var(--spacing-xl) var(--spacing-lg) var(--spacing-lg);
	}

	.message {
		margin: 0;
		font-size: var(--font-size-sm);
		font-weight: var(--font-weight-medium);
		line-height: var(--line-height-normal);
		color: var(--neutral-800);
	}

	.features {
		margin: 0;
		padding: var(--spacing-sm) var(--spacing-md);
		list-style: none;
		display: flex;
		flex-direction: column;
		gap: var(--spacing-sm);
		border: var(--border-width-thin) solid var(--neutral-100);
		border-radius: var(--border-radius-md);
		background: var(--neutral-50);
		font-size: var(--font-size-small);
		color: var(--neutral-800);

		li {
			display: flex;
			align-items: center;
			gap: var(--spacing-sm);
		}

		.icon {
			display: inline-flex;
			align-items: center;
			justify-content: center;
			flex-shrink: 0;
			width: var(--space-24);
			height: var(--space-24);
			border-radius: var(--border-radius-sm);
			background-color: color-mix(in srgb, var(--primary-green) 10%, var(--neutral-0));
			color: var(--primary-green);
		}
	}

	.providers {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-sm);
	}

	.provider {
		display: flex;
		align-items: center;
		justify-content: center;
		gap: var(--spacing-sm);
		width: 100%;
		height: var(--space-40);
		padding: 0 var(--spacing-lg);
		border: var(--border-width-thin) solid var(--neutral-200);
		border-radius: var(--border-radius-md);
		background: var(--neutral-0);
		color: var(--neutral-900);
		font-size: var(--font-size-small);
		font-weight: var(--font-weight-medium);
		cursor: pointer;
		transition:
			background-color 0.15s ease,
			border-color 0.15s ease;

		&:hover:not(:disabled) {
			background: var(--neutral-50);
			border-color: var(--neutral-400);
		}

		&:focus-visible {
			outline: 2px solid var(--primary-green);
			outline-offset: 2px;
		}

		&:disabled {
			opacity: 0.5;
			cursor: not-allowed;
		}

		svg {
			flex-shrink: 0;
		}
	}

	.info-message {
		padding: var(--spacing-smd) var(--spacing-md);
		border-radius: var(--border-radius-md);
		background-color: var(--accent-info-background);
		color: var(--accent-info);
		font-size: var(--font-size-small);
		text-align: center;
	}

	.error-message {
		color: var(--accent-warning);
		font-size: var(--font-size-small);
		text-align: center;
	}

	.guest {
		align-self: center;
		color: var(--neutral-600);
	}

	.privacy {
		align-self: center;
		margin-top: var(--spacing-sm);
		font-size: var(--font-size-xs);
		font-weight: var(--font-weight-regular);
		color: var(--neutral-600);
		text-decoration: underline;
	}
}

@media (max-width: 640px) {
	.login-wrapper {
		padding: var(--spacing-xl) var(--spacing-2xl);
	}

	.login-wrapper {
		.photo-header {
			height: var(--space-80);

			h1 {
				left: calc(var(--spacing-md) + var(--space-10));
				right: var(--spacing-md);
				bottom: calc(var(--space-24) + var(--spacing-sm));
				font-size: var(--font-size-medium);
			}
		}

		.brand-mark {
			left: var(--spacing-md);
		}

		.login-content {
			padding: var(--spacing-xl) var(--spacing-md) var(--spacing-md);
		}
	}
}
</style>
